import { useState } from "react";
import { LuPackage } from "react-icons/lu";
import { RANKINGS_API_BASE_URL } from "../../../api/rankingsApi";
import { colors } from "../../../design/tokens";

interface AppIconProps {
  src: string;
  alt: string;
  size?: "small" | "medium" | "large";
  lazy?: boolean;
  className?: string;
  onLoadError?: () => void;
  onLoadSuccess?: () => void;
}

const SIZES = {
  small: { iconSize: 24, className: "w-12 h-12" },
  medium: { iconSize: 32, className: "w-16 h-16" },
  large: { iconSize: 40, className: "w-20 h-20" },
};

export function AppIcon({
  src,
  alt,
  size = "small",
  lazy = true,
  className = "",
  onLoadError,
  onLoadSuccess,
}: AppIconProps) {
  const sizeConfig = SIZES[size];

  // Convert relative icon paths to absolute URLs using the rankings API base
  const absoluteIconUrl = src.startsWith("http")
    ? src
    : `${RANKINGS_API_BASE_URL}${src}`;

  const [isLoaded, setIsLoaded] = useState(() => {
    // Check if image is already in browser cache to avoid skeleton flash
    const img = new Image();
    img.src = absoluteIconUrl;
    return img.complete && img.naturalWidth > 0;
  });
  const [hasError, setHasError] = useState(false);

  const handleLoadSuccess = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoadSuccess?.();
  };

  const handleLoadError = () => {
    setHasError(true);
    onLoadError?.();
  };

  return (
    <div className={`relative inline-block flex-shrink-0 ${sizeConfig.className} ${className}`}>
      {!hasError && (
        <div
          className={`absolute inset-0 ${colors.surface.secondary} rounded-xl`}
        />
      )}

      {!hasError && (
        <img
          src={absoluteIconUrl}
          alt={alt}
          className={`relative w-full h-full rounded-xl object-cover transition-opacity duration-200 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading={lazy ? "lazy" : "eager"}
          referrerPolicy="no-referrer"
          onLoad={handleLoadSuccess}
          onError={handleLoadError}
        />
      )}

      {hasError && (
        <div
          className={`w-full h-full rounded-xl ${colors.surface.secondary} flex items-center justify-center`}
        >
          <LuPackage size={sizeConfig.iconSize} className={colors.text.secondary} />
        </div>
      )}
    </div>
  );
}
